// FilterWithCalendar.jsx
import React, {useState, useRef, useEffect, useId} from 'react';
import {observer} from 'mobx-react';
import classNames from 'classnames';
import css from './styles.module.scss';
import cssPicker from '../../../../styles/CustomPicker.module.scss';
import {useStore} from '../../../../store/store';
import {useResolvedThemeMode} from '../../../hooks/useResolvedThemeMode';
import DatePicker from 'react-datepicker';
import {ru} from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment-timezone';
import {TrekerButton} from '../../../pages/traffic/influence/bloggers/source/Treker/TrekerButton';
import {CreateAppModal} from '../../../../_metronic/partials';
import {toast} from 'react-toastify';

const momentTZ = require('moment-timezone');

const FilterWithCalendar = observer(({item, data, searchDateRange, parent}) => {
  const id = useId();
  const store = useStore();
  const theme = useResolvedThemeMode();
  const containerRef = useRef(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Два діапазони: dateRange_1 i dateRange_2
  const [dateRange_1, setDateRange_1] = useState([null, null]);
  const [dateRange_2, setDateRange_2] = useState([null, null]);
  const [startDate_1, endDate_1] = dateRange_1;
  const [startDate_2, endDate_2] = dateRange_2;

  // Стани для зберігання часу у мілісекундах для запитів
  const [startTimeForRequest_1, setStartTimeForRequest_1] = useState('');
  const [endTimeForRequest_1, setEndTimeForRequest_1] = useState('');
  const [startTimeForRequest_2, setStartTimeForRequest_2] = useState('');
  const [endTimeForRequest_2, setEndTimeForRequest_2] = useState('');

  useEffect(() => {
    // Функція для переведення секунд у мілісекунди
    const toMs = (value) => (value ? Number(value) * 1000 : null);

    // Якщо є дані для першого діапазону
    if (data?.[item?.withDateRange?.range_1[1]] || data?.[item?.withDateRange?.range_1[2]]) {
      const startSec = data[item.withDateRange.range_1[1]];
      const endSec = data[item.withDateRange.range_1[2]];

      const start = startSec ? momentTZ.tz(toMs(startSec), 'Europe/Kiev').toDate() : null;

      const end = endSec ? momentTZ.tz(toMs(endSec), 'Europe/Kiev').toDate() : null;

      setDateRange_1([
        start,
        endSec
          ? momentTZ
              .tz(toMs(endSec) - (23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000), 'Europe/Kiev')
              .toDate()
          : null,
      ]);

      if (start) setStartTimeForRequest_1(start.getTime());
      if (end) setEndTimeForRequest_1(end.getTime());
    }
    // Якщо є дані для другого діапазону
    if (data?.[item?.withDateRange?.range_2[1]] || data?.[item?.withDateRange?.range_2[2]]) {
      const startSec = data[item.withDateRange.range_2[1]];
      const endSec = data[item.withDateRange.range_2[2]];

      const start = startSec ? momentTZ.tz(toMs(startSec), 'Europe/Kiev').toDate() : null;

      const end = endSec ? momentTZ.tz(toMs(endSec), 'Europe/Kiev').toDate() : null;

      setDateRange_2([
        start,
        endSec
          ? momentTZ
              .tz(toMs(endSec) - (23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000), 'Europe/Kiev')
              .toDate()
          : null,
      ]);

      if (start) setStartTimeForRequest_2(start.getTime());
      if (end) setEndTimeForRequest_2(end.getTime());
    }
  }, [data, item]);

  // Закриття дропдауна при кліку поза ним (без врахування модалки)
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      const inDropdown = containerRef.current && containerRef.current.contains(e.target);
      const inModal = !!e.target.closest('.my-modal-root');
      if (!inDropdown && !inModal) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen, modalOpen]);

  // Якщо інший фільтр отримав фокус у таблиці – відкриваємо цей дропдаун
  useEffect(() => {
    if (store.focusedTableFilter === id) {
      setDropdownOpen(true);
      store.setFocusFilter('');
    }
  }, [store.focusedTableFilter, id, store]);

  const handleBlur = () => {
    setTimeout(() => {
      if (
        !modalOpen &&
        containerRef.current &&
        !containerRef.current.contains(document.activeElement)
      ) {
        setDropdownOpen(false);
      }
    }, 50);
  };

  // Функція для отримання мілісекунд для півночі в Києві з GMT
  const getMillisecondsForMidnightInKievFromGMT = (h, m, s, date) => {
    const kievTime = momentTZ(date).tz('Europe/Kiev');
    const utcTime = kievTime.clone().utc();
    utcTime.hours(h);
    utcTime.minutes(m);
    utcTime.seconds(s);
    const milliseconds = utcTime.valueOf();
    return milliseconds;
  };

  // Обробник зміни дати для першого календаря
  const onChange_1 = (dates, e) => {
    let [start, end] = dates;

    if (!end) {
      setDateRange_1([start, null]);
      const yearStart = start.getFullYear();
      const monthStart = start.getMonth();
      const dayStart = Number(e.target.innerText);
      const selectDateStart = new Date(Date.UTC(yearStart, monthStart, dayStart, 0, 0, 0));
      const midnightInMillisStart = getMillisecondsForMidnightInKievFromGMT(
        0,
        0,
        0,
        selectDateStart
      );
      setEndTimeForRequest_1('');
      setStartTimeForRequest_1(midnightInMillisStart);
    }
    if (!!end) {
      const yearEnd = end.getFullYear();
      const monthEnd = end.getMonth();
      const dayEnd = Number(e.target.innerText);
      const selectDateEnd = new Date(Date.UTC(yearEnd, monthEnd, dayEnd, 0, 0, 0));
      const midnightInMillisEnd = getMillisecondsForMidnightInKievFromGMT(
        23,
        59,
        59,
        selectDateEnd
      );
      setDateRange_1([start, end]);
      setEndTimeForRequest_1(midnightInMillisEnd);
    }
  };

  // Обробник зміни дати для другого календаря
  const onChange_2 = (dates, e) => {
    let [start, end] = dates;

    if (!end) {
      setDateRange_2([start, null]);
      const yearStart = start.getFullYear();
      const monthStart = start.getMonth();
      const dayStart = Number(e.target.innerText);
      const selectDateStart = new Date(Date.UTC(yearStart, monthStart, dayStart, 0, 0, 0));
      const midnightInMillisStart = getMillisecondsForMidnightInKievFromGMT(
        0,
        0,
        0,
        selectDateStart
      );
      setEndTimeForRequest_2('');
      setStartTimeForRequest_2(midnightInMillisStart);
    }
    if (!!end) {
      const yearEnd = end.getFullYear();
      const monthEnd = end.getMonth();
      const dayEnd = Number(e.target.innerText);
      const selectDateEnd = new Date(Date.UTC(yearEnd, monthEnd, dayEnd, 0, 0, 0));
      const midnightInMillisEnd = getMillisecondsForMidnightInKievFromGMT(
        23,
        59,
        59,
        selectDateEnd
      );
      setDateRange_2([start, end]);
      setEndTimeForRequest_2(midnightInMillisEnd);
    }
  };

  // При натисканні «Застосувати фільтр»
  const handleApply = () => {
    setModalOpen(false);

    // 23:59:59 у секундах
    const diff = 23 * 60 * 60 + 59 * 60 + 59; // = 86399 секунд

    // Якщо startTimeForRequest_* у мілісекундах, спочатку ділимо на 1000 для отримання секунд
    const toSec = (value) => (value ? Math.floor(value / 1000) : null);

    const start_1_sec = toSec(startTimeForRequest_1);
    const end_1_sec = endTimeForRequest_1
      ? toSec(endTimeForRequest_1)
      : start_1_sec !== null
      ? start_1_sec + diff
      : null;

    const start_2_sec = toSec(startTimeForRequest_2);
    const end_2_sec = endTimeForRequest_2
      ? toSec(endTimeForRequest_2)
      : start_2_sec !== null
      ? start_2_sec + diff
      : null;

    searchDateRange(
      {
        [item.withDateRange.range_1[1]]: start_1_sec,
        [item.withDateRange.range_1[2]]: end_1_sec,
        [item.withDateRange.range_2[1]]: start_2_sec,
        [item.withDateRange.range_2[2]]: end_2_sec,
      },
      parent
    );
  };

  const formatDate = (date) => (date ? momentTZ(date).tz('Europe/Kiev').format('DD.MM.YYYY') : '');

  const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  return (
    <div
      className={css.container}
      ref={containerRef}
      tabIndex={0}
      onBlur={handleBlur}
      style={{
        '--bg': theme === 'dark' ? 'rgba(27, 29, 35, 1)' : '#FFFFFF',
        '--borderColor': theme === 'dark' ? 'hsl(0, 0%, 20%)' : 'hsl(0, 0%, 80%)',
        '--itemHover': theme === 'dark' ? '#4d5863' : '#f4f6f9',
        '--buttonHover': theme === 'dark' ? 'rgba(258, 258, 258, 0.05)' : 'rgba(0, 0, 0, 0.05)',
      }}
    >
      {/* Кнопка, яка відкриває дропдаун */}
      <label className={css.buttonOpen}>
        <span className={css.buttonOpenTitle}>{item?.label || ''}</span>
        <input
          checked={dropdownOpen}
          type='checkbox'
          style={{display: 'none'}}
          onChange={() => setDropdownOpen((v) => !v)}
        />
        <svg className={css.arrow} width='12' height='7' fill='none' viewBox='0 0 12 7'>
          <path
            fill={theme === 'dark' ? 'hsl(0, 0.00%, 100.00%)' : 'hsl(0, 0.00%, 0.00%)'}
            d='M5.59 6.077a1.66 1.66 0 0 1-1.18-.487L0 1.178 1.178 0 5.59 4.41 10 0l1.178 1.178-4.41 4.411a1.66 1.66 0 0 1-1.179.488Z'
          />
        </svg>
        <span className={css.hoverEffect}></span>
      </label>

      {/* Якщо дропдаун відкритий, показуємо кнопку для обох діапазонів і кнопку «Застосувати фільтр» */}
      {dropdownOpen && (
        <div
          className={css.dropdown}
          style={{flexDirection: 'column', gap: '4px', padding: '8px', zIndex: '1000'}}
        >
          <TrekerButton
            svg={<></>}
            style={{whiteSpace: 'nowrap'}}
            onClick={() => setModalOpen(true)}
          >
            {isSameDay(startDate_1, endDate_1)
              ? `${item.withDateRange.range_1[0]}: ${formatDate(startDate_1)}`
              : `${item.withDateRange.range_1[0]}: ${formatDate(startDate_1)} - ${formatDate(
                  endDate_1
                )}`}
          </TrekerButton>
          <TrekerButton
            svg={<></>}
            style={{whiteSpace: 'nowrap'}}
            onClick={() => setModalOpen(true)}
          >
            {isSameDay(startDate_2, endDate_2)
              ? `${item.withDateRange.range_2[0]}: ${formatDate(startDate_2)}`
              : `${item.withDateRange.range_2[0]}: ${formatDate(startDate_2)} - ${formatDate(
                  endDate_2
                )}`}
          </TrekerButton>
          <ActiveBtn onClick={handleApply} />
        </div>
      )}

      {/* Модальне вікно з двома календарями */}
      <CreateAppModal
        show={modalOpen}
        handleClose={() => setModalOpen(false)}
        showHeader={false}
        className='my-modal-root'
        customWidth='mw-450px'
        padding='p2'
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* Перший календар */}
          <div
            className={css.dateContainer}
            style={{display: 'flex', alignItems: 'center', flexDirection: 'column'}}
          >
            <span className='fs-2 mb-2'>{item.withDateRange.range_1[0]}</span>
            <DatePicker
              calendarClassName={classNames(
                cssPicker.container,
                cssPicker[`_${theme}`],
                theme === 'dark' ? cssPicker.dark : cssPicker.light,
                cssPicker._transparent
              )}
              calendarStartDay={1}
              locale={ru}
              selectsRange
              startDate={startDate_1}
              endDate={endDate_1}
              onChange={onChange_1}
              inline
            />
          </div>

          {/* Другий календар */}
          <div
            className={classNames(css.dateContainer, 'mt-5')}
            style={{display: 'flex', alignItems: 'center', flexDirection: 'column'}}
          >
            <span className='fs-2 mb-2'>{item.withDateRange.range_2[0]}</span>
            <DatePicker
              calendarClassName={classNames(
                cssPicker.container,
                cssPicker[`_${theme}`],
                theme === 'dark' ? cssPicker.dark : cssPicker.light,
                cssPicker._transparent
              )}
              calendarStartDay={1}
              locale={ru}
              selectsRange
              startDate={startDate_2}
              endDate={endDate_2}
              onChange={onChange_2}
              inline
            />
          </div>
        </div>
        <div>
          <ActiveBtn onClick={handleApply} />
        </div>
      </CreateAppModal>
    </div>
  );
});

const ActiveBtn = ({onClick}) => (
  <TrekerButton
    style={{
      height: '36px',
      width: '100%',
      paddingTop: '0',
      paddingBottom: '0',
      display: 'flex',
      justifyContent: 'center',
    }}
    svg={
      <svg width='18' height='18' viewBox='0 0 32 32'>
        <path
          d='M3.241,7.646L13,19v9l6-4v-5l9.759-11.354C29.315,6.996,28.848,6,27.986,6H4.014C3.152,6,2.685,6.996,3.241,7.646z'
          fill='none'
          stroke='#000000'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeMiterlimit='10'
          strokeWidth='2'
        />
      </svg>
    }
    onClick={onClick}
  >
    Застосувати фільтр
  </TrekerButton>
);

export default FilterWithCalendar;
